import BridgeFormControl from './BridgeFormControl';
import dom from './util/dom';
import Attr from './Attr';
import BridgeFormUtilConfig from './BridgeFormUtilConfig';
import createLogger from './util/createLogger';

/**
 * @typedef FieldGroup
 * @type {object}
 * @property {string} legend HTML content for generated `legend`
 * @property {string[]} fields Ordered list of field names to include in this `fieldset`
 */

/**
 * @typedef CustomFormData
 * @type {object}
 * @property {string} form Custom form id, e.g. `CT_20`
 * @property {string[]} fields Custom form field names
 * @property {HTMLHeadingElement[]} $titles `h4` elements (if any) created via `Additional Title` fields
 * @property {HTMLParagraphElement[]} $texts `p` elements (if any) created via `Additional Text` fields
 */

/**
 * @callback UpdateFieldGroupsCallback
 * @param {FieldGroup[]|FieldGroup} fieldGroups
 */

 /**
 * @typedef RunResult
 * @type {object}
 * @property {CustomFormData} customFormData
 * @property {UpdateFieldGroupsCallback} updateFieldGroups
 */


const formKeySuffix = '_FormKey';

/**
 * @param {BridgeFormControl} control
 * @param {string} formKey
 * @private
 */
const testControlBelongsToCustomForm = ({ name }, formKey) => name.startsWith(formKey);

/** @private */
function getForms() {
  const ignoreList = [
    '#tn-apply-promo',
  ];

  /** @type {HTMLFormElement[]} */
  const $forms = [];

  /** @type {HTMLFormElement[]} */
  const $skippedForms = [];

  dom.findAll('form')
    .forEach(($form) => {
      const ignore = ignoreList.some(selector => dom.matches($form, selector));

      if (ignore) {
        $skippedForms.push($form);
      } else {
        $forms.push($form);
      }
    });

  return {
    $forms,
    $skippedForms,
  };
}

/**
 * @param {HTMLInputElement} $keyInput
 * @private
 */
function getTitlesAndTextElements($keyInput) {
  /** @type {HTMLParagraphElement} */
  const $texts = [];
  /** @type {HTMLHeadingElement} */
  const $titles = [];

  Array.from($keyInput.parentElement.children)
    .forEach(($el) => {
      const tagName = $el.tagName.toLowerCase();

      if (tagName === 'p') {
        $texts.push($el);
        return;
      }

      if (tagName === 'h4') {
        $titles.push($el);
      }
    });

  return { $titles, $texts };
}

/**
 * @param {HTMLFormElement} $form
 * @private
 */
function getControls($form) {
  return dom.findAllFrom($form, `[${Attr.Data.ControlGroupFor}]`)
    .map($el => new BridgeFormControl($el));
}

/**
 * @param {HTMLFormElement} $form
 * @returns {HTMLInputElement}
 * @private
 */
function getKeyInput($form) {
  return dom.findAllFrom($form, 'input[type="hidden"]')
    .find($el => $el.name.endsWith(formKeySuffix));
}

/** @private */
function groupControlsByShortName(formKey, controls) {
  const map = {};

  controls.forEach((control) => {
    const name = control.getShortName(formKey);

    if (map[name]) {
      map[name].push(control);
    } else {
      map[name] = [control];
    }
  });

  return map;
}



/**
 * @returns {RunResult}
 */
function run() {
  const config = Object.assign({}, BridgeFormUtilConfig);

  const logger = createLogger(config);
  logger.group('Running Form Bridge Util');

  logger.log('Settings:');
  logger.printJson(config);

  const { $forms, $skippedForms } = getForms();

  if ($skippedForms.length) {
    logger.log('Skipping forms:\n', $skippedForms);
  }

  if (!$forms.length) {
    logger.log('No forms to process, finishing');
  }

  /** @type {CustomFormData} */
  let customFormData;

  /** @type {UpdateFieldGroupsCallback} */
  let updateFieldGroups;

  $forms.forEach(($form) => {
    logger.group(`Patching controls for form #${$form.id}`);
    logger.log($form);

    const controls = getControls($form);
    const $keyInput = getKeyInput($form);

    if (controls.length) {
      logger.log('Patched controls:\n');
      logger.printJson(controls.map(c => c.name));
    } else {
      logger.log('No controls patched');
    }

    if (!$keyInput) {
      logger.groupEnd();
      return;
    }

    const key = $keyInput.value;

    const customControls = controls.filter(c => testControlBelongsToCustomForm(c, key));

    const controlsMap = groupControlsByShortName(key, customControls);

    const { $titles, $texts } = getTitlesAndTextElements($keyInput);

    /** @type {CustomFormData} */
    customFormData = {
      form: key,
      fields: [...Object.keys(controlsMap)],
      $titles,
      $texts,
    };

    updateFieldGroups = (res) => {
      logger.group(`Running updateFieldGroups() for ${customFormData.form}:`);

      const isValid = validateFieldGroupConfig(res);

      if (!isValid) {
        logger.log('Invalid field group configuration:', res);
        logger.log('Cancelling update');
        logger.groupEnd();
        return;
      }

      /** @type {FieldGroup[]} */
      const fieldGroups = [].concat(res);

      logger.log('Updating field groups to the following:');
      logger.printJson(res);

      clearCustomForm($keyInput);

      /** @type {Element} */
      let $insertEl = $keyInput;
      let $fieldset;

      if ($keyInput) {
        $fieldset = dom.closest($keyInput, 'fieldset');
      }

      fieldGroups.forEach((group, index) => {
        const $legend = createLegend(group.legend, config);

        if (index === 0 && $fieldset) {
          $insertEl = $fieldset;
        } else {
          $fieldset = createFieldset(key, index, config);
          $insertEl.insertAdjacentElement('afterend', $fieldset);
        }

        $fieldset.insertAdjacentElement('afterbegin', $legend);

        group.fields.forEach((fieldName) => {
          const fieldControls = controlsMap[fieldName];
          delete controlsMap[fieldName];

          if (fieldControls) {
            fieldControls.forEach(control => $fieldset.appendChild(control.$group));
          }
        });

        $insertEl = $fieldset;
      });

      const unassigned = [...Object.keys(controlsMap)];

      if (unassigned.length) {
        console.warn(`${unassigned.length} fields not assigned a fieldset; inserting them into last fieldset:`, unassigned);

        Object.values(controlsMap).forEach((fieldControls) => {
          fieldControls.forEach(control => $fieldset.appendChild(control.$group));
        });
      }

      logger.log('Update complete');
      logger.groupEnd();
    };

    logger.groupEnd();
  });

  if (customFormData) {
    logger.log('Returning custom form configuration:');
    logger.log(customFormData);
  }

  logger.groupEnd();

  return {
    customForm: customFormData,
    updateFieldGroups,
  };
}


/**
 * @param {FieldGroup} res
 * @private
 */
function validateFieldGroupConfig(res) {
  const test = o => Array.isArray(o.fields)
    && o.fields.length
    && !!o.legend;

  const list = [].concat(res);

  return list.length > 0 && list.every(test);
}

/**
 * @param {HTMLInputElement} $keyInput
 * @private
 */
function clearCustomForm($keyInput) {
  Array.from($keyInput.parentElement.childNodes).forEach(($el) => {
    if ($el !== $keyInput) {
      dom.remove($el);
    }
  });
}

/**
 * @param {string} formKey
 * @param {number} index
 * @param {BridgeFormUtilConfig} config
 * @private
 */
function createFieldset(formKey, index, config) {
  const $el = document.createElement('fieldset');

  $el.setAttribute(Attr.Class, config.fieldsetClassName);
  $el.setAttribute(Attr.Data.Tn.BridgeFormKey, formKey);
  $el.setAttribute(Attr.Data.Tn.BridgeFieldsetIndex, index);

  return $el;
}

/**
 * @param {string} content
 * @param {BridgeFormUtilConfig} config
 * @private
 */
function createLegend(content, config) {
  const $el = document.createElement('legend');

  $el.setAttribute(Attr.Class, config.legendClassName);
  $el.innerHTML = content;

  return $el;
}


export default run;
